import React, { useEffect, useState } from 'react';
import useKraQuarterlyStore from '../../store/kraQuarterlyStore';
import { getDepartments } from '../../services/employeeService';
import KRAFilters, { matchesName } from '../../components/KRAFilters';

// Quarters follow the Indian financial year (April-March). `year` is the year the
// FY STARTS in: 2026 = FY 2026-27, so Q4 (Jan-Mar) of that FY falls in 2027.
const QUARTER_MONTHS = { 1: 'Apr–Jun', 2: 'Jul–Sep', 3: 'Oct–Dec', 4: 'Jan–Mar' };
const fyLabel = (startYear) => `FY ${startYear}-${String(startYear + 1).slice(-2)}`;

const currentFinancialYear = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};
// getMonth(): Apr = 3 -> Q1, Jul = 6 -> Q2, Oct = 9 -> Q3, Jan = 0 -> Q4
const currentQuarter = () => Math.floor(((new Date().getMonth() + 9) % 12) / 3) + 1;

const QuarterlyDashboardPage = () => {
  const { dashboard, isLoading, fetchDashboard } = useKraQuarterlyStore();
  const [year, setYear] = useState(currentFinancialYear());
  const [quarter, setQuarter] = useState(currentQuarter());
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [nameFilter, setNameFilter] = useState('');

  // Period is already chosen by the quarter/year/department pickers above
  // (server-side), so only name needs narrowing here - no separate month filter.
  const visibleDashboard = dashboard.filter((emp) => matchesName(emp.employee_name, nameFilter));

  useEffect(() => {
    getDepartments().then((res) => setDepartments(res.data.departments || []));
  }, []);

  useEffect(() => {
    fetchDashboard(year, quarter, departmentId || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, quarter, departmentId]);

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-bold text-white">CEO Quarterly Review</h1>
          <div className="flex gap-2">
            <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className="border border-gray-300 rounded px-3 py-2 text-sm">
              {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q} ({QUARTER_MONTHS[q]})</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-gray-300 rounded px-3 py-2 text-sm">
              {Array.from({ length: 6 }, (_, i) => currentFinancialYear() + 1 - i).map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
            </select>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm">
              <option value="">All Departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>

        <KRAFilters
          name={nameFilter}
          onNameChange={setNameFilter}
          showMonth={false}
          shown={visibleDashboard.length}
          total={isLoading ? null : dashboard.length}
        />

        {isLoading ? (
          <p className="text-gray-500">Loading...</p>
        ) : dashboard.length === 0 ? (
          <div className="glass-card p-6 text-sm text-gray-500">No KRA records for Q{quarter} {fyLabel(year)} ({QUARTER_MONTHS[quarter]}) yet.</div>
        ) : visibleDashboard.length === 0 ? (
          <div className="glass-card p-6 text-sm text-gray-500">No one matches "{nameFilter.trim()}".</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleDashboard.map((emp) => (
              <div key={emp.employee_id} className="glass-card p-4">
                <div className="font-semibold text-gray-900">{emp.employee_name}</div>
                <div className="flex gap-2 my-3">
                  {emp.monthly_scores.map((m) => (
                    <div key={m.month} className="flex-1 text-center bg-gray-50 rounded py-2">
                      <div className="text-xs text-gray-400">{new Date(m.month).toLocaleDateString('en-IN', { month: 'short' })}</div>
                      <div className="text-sm font-bold text-gray-900">
                        {m.status === 'locked' ? m.final_score : <span className="text-red-600 text-[10px]">Not locked</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {emp.quarterly_average ?? '—'} <span className="text-xs font-normal text-gray-400">Q avg</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuarterlyDashboardPage;
